import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ProgressBar } from "@/components/domain/Bits";
import { fmtDateTime, num } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, X, Vote, Users, MessageSquareText } from "lucide-react";

const BLANK = { question: "¿Qué premio te gustaría que rifemos próximamente?", options: ["", ""], allow_other: true, active: true, show_results: true };

export default function AdminPolls() {
  const [polls, setPolls] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [votesFor, setVotesFor] = useState(null);
  const [votes, setVotes] = useState([]);
  const load = () => api.get("/admin/polls").then((r) => setPolls(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setOpt = (i, v) => setF({ ...f, options: f.options.map((o, j) => (j === i ? v : o)) });
  const save = async () => {
    try { await api.post("/admin/polls", f); toast.success("Encuesta creada"); setOpen(false); setF(BLANK); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo crear"); }
  };
  const patch = async (p, upd) => { await api.patch(`/admin/polls/${p.id}`, upd); await load(); };
  const remove = async (p) => {
    if (!window.confirm("¿Eliminar esta encuesta y todos sus votos?")) return;
    await api.delete(`/admin/polls/${p.id}`); toast.success("Encuesta eliminada"); await load();
  };
  const showVotes = async (p) => { setVotesFor(p); setVotes((await api.get(`/admin/polls/${p.id}/votes`)).data); };
  const exportCSV = () => {
    const rows = [["Nombre", "Correo", "WhatsApp", "Opción", "Propuesta", "Fecha"], ...votes.map((v) => [v.name, v.email, v.whatsapp, v.option_text, v.other_text, v.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "votos.csv"; a.click();
  };
  return (
    <AdminLayout title="Encuestas de premios">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-slate-500">Pregunta a tus clientes qué premio quieren. Solo una encuesta puede estar activa en el sitio público.</p>
        <Button onClick={() => { setF(BLANK); setOpen(true); }} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-poll-btn"><Plus className="mr-1.5 h-4 w-4" />Nueva encuesta</Button>
      </div>
      <div className="space-y-5">
        {polls.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">Aún no hay encuestas.</div>}
        {polls.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`poll-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-900">{p.question}</h3>
                  {p.active && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Activa</span>}
                </div>
                <p className="mt-1 text-xs text-slate-400">{num(p.total_votes)} votos · creada {fmtDateTime(p.created_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-500"><Switch checked={p.active} onCheckedChange={(v) => patch(p, { active: v })} data-testid={`poll-active-${p.id}`} />Activa</label>
                <label className="flex items-center gap-2 text-xs text-slate-500"><Switch checked={p.show_results} onCheckedChange={(v) => patch(p, { show_results: v })} data-testid={`poll-results-${p.id}`} />Resultados públicos</label>
                <Button variant="outline" size="sm" onClick={() => showVotes(p)} data-testid={`poll-votes-${p.id}`}><Users className="mr-1.5 h-4 w-4" />Participantes</Button>
                <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(p)} data-testid={`poll-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {p.options.map((o) => (
                <div key={o.id}>
                  <div className="flex justify-between text-sm"><span className="text-slate-700">{o.text}</span><span className="font-mono text-slate-500">{o.votes} · {o.pct}%</span></div>
                  <ProgressBar value={o.pct} className="mt-1" />
                </div>
              ))}
              {p.allow_other && <p className="text-xs text-slate-400">Incluye campo libre para otras propuestas.</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="poll-dialog">
          <DialogHeader><DialogTitle>Nueva encuesta</DialogTitle><DialogDescription>Al activarla se desactiva la encuesta anterior.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Pregunta</Label><Input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="poll-question" /></div>
            <div>
              <Label>Opciones de premio</Label>
              <div className="mt-1.5 space-y-2">
                {f.options.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Opción ${i + 1}`} data-testid={`poll-option-${i}`} />
                    {f.options.length > 2 && <Button type="button" variant="outline" size="icon" onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, options: [...f.options, ""] })} data-testid="poll-add-option"><Plus className="mr-1 h-3.5 w-3.5" />Agregar opción</Button>
              </div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={f.allow_other} onCheckedChange={(v) => setF({ ...f, allow_other: v })} data-testid="poll-allow-other" /><Label>Permitir escribir otra propuesta</Label></div>
            <div className="flex items-center gap-3"><Switch checked={f.show_results} onCheckedChange={(v) => setF({ ...f, show_results: v })} /><Label>Mostrar resultados al público después de votar</Label></div>
            <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /><Label>Activar de inmediato</Label></div>
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="poll-save">Crear encuesta</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!votesFor} onOpenChange={(o) => !o && setVotesFor(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="poll-votes-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Vote className="h-5 w-5 text-brand" />Participantes · {votesFor?.question}</DialogTitle><DialogDescription>{votes.length} votos registrados. Úsalos como prospectos.</DialogDescription></DialogHeader>
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={exportCSV} disabled={!votes.length} data-testid="poll-export">Exportar CSV</Button></div>
          <table className="mt-3 w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-400"><tr><th className="py-2">Nombre</th><th>Contacto</th><th>Voto</th><th>Fecha</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {votes.map((v) => (
                <tr key={v.id}>
                  <td className="py-2 font-medium text-slate-900">{v.name}</td>
                  <td className="text-xs text-slate-500">{v.email}{v.whatsapp ? ` · ${v.whatsapp}` : ""}</td>
                  <td className="text-slate-700">{v.option_text}{v.other_text && <div className="flex items-start gap-1 text-xs italic text-slate-500"><MessageSquareText className="mt-0.5 h-3 w-3 shrink-0" />{v.other_text}</div>}</td>
                  <td className="text-xs text-slate-400">{fmtDateTime(v.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
